export const DISCOVERY_CATEGORIES = [
  "flights", "stays", "cars", "cruises", "tours", "activities", "events", "restaurants",
  "lounges", "day_rooms", "sleep_pods", "luggage", "transfers", "trains", "buses", "ferries",
  "local_boat_transport", "local_guides", "vip", "offers", "shopping", "pharmacies", "urgent_care",
  "hospitals", "police", "embassies", "lost_and_found", "atms", "restrooms", "charging", "wifi",
  "convenience_stores", "transit", "rideshare_pickup", "bike_scooter"
] as const;

export type DiscoveryCategory = (typeof DISCOVERY_CATEGORIES)[number];
export type CommerceTier = "FREE" | "INSIDER" | "CREATOR" | "LIFETIME_CREATOR";

function requireText(value: string, field: string) {
  if (value.trim().length === 0) throw new Error(`${field} must not be blank`);
  return value;
}

function canonicalIso(value: string, field: string) {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed) || new Date(parsed).toISOString() !== value) throw new Error(`${field} must be canonical ISO`);
  return value;
}

function minor(value: number, field: string) {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${field} must use non-negative integer minor units`);
  return value;
}

function currency(value: string) {
  if (!/^[A-Z]{3}$/.test(value)) throw new Error(`Invalid currency: ${value}`);
  return value;
}

export interface FxQuoteInput {
  readonly tier: CommerceTier;
  readonly sourceAmountMinor: number;
  readonly sourceCurrency: string;
  readonly targetCurrency: string;
  readonly rateNumerator: number;
  readonly rateDenominator: number;
  readonly rateSource: string;
  readonly rateObservedAt: string;
}

const FX_FEE_BASIS_POINTS: Readonly<Record<CommerceTier, number>> = Object.freeze({
  FREE: 80,
  INSIDER: 40,
  CREATOR: 20,
  LIFETIME_CREATOR: 20
});

export function createFxQuote(input: FxQuoteInput) {
  minor(input.sourceAmountMinor, "sourceAmountMinor");
  currency(input.sourceCurrency);
  currency(input.targetCurrency);
  if (!Number.isSafeInteger(input.rateNumerator) || input.rateNumerator <= 0) throw new Error("rateNumerator must be a positive integer");
  if (!Number.isSafeInteger(input.rateDenominator) || input.rateDenominator <= 0) throw new Error("rateDenominator must be a positive integer");
  requireText(input.rateSource, "rateSource");
  canonicalIso(input.rateObservedAt, "rateObservedAt");
  const convertedBeforeFeeMinor = Math.floor((input.sourceAmountMinor * input.rateNumerator) / input.rateDenominator);
  const feeBasisPoints = FX_FEE_BASIS_POINTS[input.tier];
  const feeMinor = Math.floor((convertedBeforeFeeMinor * feeBasisPoints) / 10_000);
  return Object.freeze({
    ...input,
    convertedBeforeFeeMinor,
    feeBasisPoints,
    feeMinor,
    targetAmountMinor: convertedBeforeFeeMinor - feeMinor,
    rounding: "floor_minor_unit" as const
  });
}

export type CheckoutTransactionType = "REAL_WORLD_SERVICE" | "MEMBERSHIP" | "MOBILE_DIGITAL_GOOD";
export type CheckoutPaymentMethod = "APPLE_PAY" | "GOOGLE_PAY" | "CARD" | "XAMAN" | "APPLE_IAP" | "GOOGLE_PLAY_BILLING";

export function createCheckoutQuote(input: {
  readonly transactionType: CheckoutTransactionType;
  readonly tier: CommerceTier;
  readonly subtotalMinor: number;
  readonly taxesMinor: number;
  readonly feesMinor: number;
  readonly memberDiscountMinor: number;
  readonly memberDiscountEvidence: string | null;
  readonly pointsRedeemed: number;
  readonly pointsEligible: boolean;
  readonly currency: string;
  readonly supportedPaymentMethods: readonly CheckoutPaymentMethod[];
}) {
  for (const [field, value] of Object.entries({ subtotalMinor: input.subtotalMinor, taxesMinor: input.taxesMinor, feesMinor: input.feesMinor, memberDiscountMinor: input.memberDiscountMinor, pointsRedeemed: input.pointsRedeemed })) minor(value, field);
  currency(input.currency);
  if (input.memberDiscountMinor > 0 && (input.tier === "FREE" || input.memberDiscountEvidence === null || input.memberDiscountEvidence.trim().length === 0)) throw new Error("Member discount requires an eligible tier and evidence");
  const beforeDiscountMinor = input.subtotalMinor + input.taxesMinor + input.feesMinor;
  if (input.memberDiscountMinor + input.pointsRedeemed > beforeDiscountMinor) throw new Error("Discount and points cannot exceed checkout total");
  const allowed = input.transactionType === "REAL_WORLD_SERVICE"
    ? new Set<CheckoutPaymentMethod>(["APPLE_PAY", "GOOGLE_PAY", "CARD", "XAMAN"])
    : new Set<CheckoutPaymentMethod>(["APPLE_IAP", "GOOGLE_PLAY_BILLING"]);
  if (input.supportedPaymentMethods.length === 0 || input.supportedPaymentMethods.some((method) => !allowed.has(method))) throw new Error("Payment method does not match transaction type");
  const totalMinor = beforeDiscountMinor - input.memberDiscountMinor - input.pointsRedeemed;
  return Object.freeze({
    ...input,
    beforeDiscountMinor,
    pointsRedemptionValueMinor: input.pointsRedeemed,
    totalMinor,
    memberDiscountAutomaticallyApplied: input.memberDiscountMinor > 0,
    projectedPoints: input.pointsEligible ? Math.floor(totalMinor / 100) : 0,
    totalShownBeforeCommitment: true as const,
    paymentAuthority: "SERVER" as const
  });
}

export type BookingStatus =
  | "REQUESTED"
  | "CONFIRMED"
  | "CHANGE_PENDING"
  | "CANCELLATION_PENDING"
  | "CANCELLED"
  | "REFUND_PENDING"
  | "REFUNDED"
  | "FAILED";

export interface BookingRecord {
  readonly bookingId: string;
  readonly accountId: string;
  readonly provider: string;
  readonly category: DiscoveryCategory;
  readonly mode: "redirect" | "embedded" | "merchant" | "request" | "booking";
  readonly status: BookingStatus;
  readonly totalMinor: number;
  readonly currency: string;
  readonly providerReference: string | null;
  readonly cancellationTerms: string;
  readonly refundSourceOfTruth: "PORTABILITY" | "PROVIDER";
  readonly idempotencyKeys: readonly string[];
  readonly tripInsertionRequired: boolean;
  readonly updatedAt: string;
}

export function createBooking(input: Omit<BookingRecord, "status" | "providerReference" | "idempotencyKeys" | "tripInsertionRequired"> & { readonly idempotencyKey: string }) {
  requireText(input.bookingId, "bookingId");
  requireText(input.accountId, "accountId");
  requireText(input.provider, "provider");
  requireText(input.cancellationTerms, "cancellationTerms");
  requireText(input.idempotencyKey, "idempotencyKey");
  minor(input.totalMinor, "totalMinor");
  currency(input.currency);
  canonicalIso(input.updatedAt, "updatedAt");
  return Object.freeze({
    bookingId: input.bookingId,
    accountId: input.accountId,
    provider: input.provider,
    category: input.category,
    mode: input.mode,
    status: "REQUESTED" as const,
    totalMinor: input.totalMinor,
    currency: input.currency,
    providerReference: null,
    cancellationTerms: input.cancellationTerms,
    refundSourceOfTruth: input.refundSourceOfTruth,
    idempotencyKeys: Object.freeze([input.idempotencyKey]),
    tripInsertionRequired: false,
    updatedAt: input.updatedAt
  });
}

export type BookingEvent =
  | { readonly type: "CONFIRMED"; readonly idempotencyKey: string; readonly occurredAt: string; readonly providerReference: string }
  | { readonly type: "CHANGE_REQUESTED" | "CANCELLATION_REQUESTED" | "REFUND_REQUESTED"; readonly idempotencyKey: string; readonly occurredAt: string }
  | { readonly type: "CANCELLED" | "REFUNDED" | "FAILED"; readonly idempotencyKey: string; readonly occurredAt: string };

export function applyBookingEvent(booking: BookingRecord, event: BookingEvent): BookingRecord {
  requireText(event.idempotencyKey, "idempotencyKey");
  canonicalIso(event.occurredAt, "occurredAt");
  if (booking.idempotencyKeys.includes(event.idempotencyKey)) return booking;
  const allowed: Readonly<Record<BookingEvent["type"], readonly BookingStatus[]>> = {
    CONFIRMED: ["REQUESTED", "CHANGE_PENDING"],
    CHANGE_REQUESTED: ["CONFIRMED"],
    CANCELLATION_REQUESTED: ["CONFIRMED", "CHANGE_PENDING"],
    REFUND_REQUESTED: ["CONFIRMED", "CANCELLED"],
    CANCELLED: ["CANCELLATION_PENDING"],
    REFUNDED: ["REFUND_PENDING", "CANCELLED"],
    FAILED: ["REQUESTED", "CHANGE_PENDING", "CANCELLATION_PENDING", "REFUND_PENDING"]
  };
  if (!allowed[event.type].includes(booking.status)) throw new Error(`Invalid booking transition ${booking.status} -> ${event.type}`);
  const statusByEvent: Readonly<Record<BookingEvent["type"], BookingStatus>> = {
    CONFIRMED: "CONFIRMED",
    CHANGE_REQUESTED: "CHANGE_PENDING",
    CANCELLATION_REQUESTED: "CANCELLATION_PENDING",
    REFUND_REQUESTED: "REFUND_PENDING",
    CANCELLED: "CANCELLED",
    REFUNDED: "REFUNDED",
    FAILED: "FAILED"
  };
  if (event.type === "CONFIRMED") requireText(event.providerReference, "providerReference");
  return Object.freeze({
    ...booking,
    status: statusByEvent[event.type],
    providerReference: event.type === "CONFIRMED" ? event.providerReference : booking.providerReference,
    idempotencyKeys: Object.freeze([...booking.idempotencyKeys, event.idempotencyKey]),
    tripInsertionRequired: event.type === "CONFIRMED" ? true : booking.tripInsertionRequired,
    updatedAt: event.occurredAt
  });
}

export interface PriceMatchSubmission {
  readonly requestId: string;
  readonly accountId: string;
  readonly provider: string;
  readonly portabilityTotalMinor: number;
  readonly competitorTotalMinor: number;
  readonly currency: string;
  readonly evidenceUrl: string;
  readonly exactSameDates: boolean;
  readonly exactSameProduct: boolean;
  readonly exactSameTaxesAndFees: boolean;
  readonly exactSameCancellationTerms: boolean;
  readonly exactSameRestrictions: boolean;
  readonly submittedAt: string;
}

const PRICE_REVIEW_INELIGIBLE_PROVIDERS = new Set([
  "booking_com",
  "booking_com_demand",
  "booking_demand"
]);

function canonicalProvider(value: string) {
  return requireText(value, "provider")
    .trim()
    .toLocaleLowerCase()
    .replace(/https?:\/\//g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function assertPriceReviewProviderEligible(provider: string) {
  const canonical = canonicalProvider(provider);
  if (PRICE_REVIEW_INELIGIBLE_PROVIDERS.has(canonical)) {
    throw new Error("Booking.com listings are not eligible for Portability price review; Booking.com pricing and support terms apply");
  }
  return canonical;
}

export function decidePriceMatch(input: PriceMatchSubmission, decision: { readonly reviewerId: string; readonly decidedAt: string; readonly approve: boolean }) {
  assertPriceReviewProviderEligible(input.provider);
  minor(input.portabilityTotalMinor, "portabilityTotalMinor");
  minor(input.competitorTotalMinor, "competitorTotalMinor");
  currency(input.currency);
  requireText(input.evidenceUrl, "evidenceUrl");
  requireText(decision.reviewerId, "reviewerId");
  canonicalIso(input.submittedAt, "submittedAt");
  canonicalIso(decision.decidedAt, "decidedAt");
  const equivalent = input.exactSameDates && input.exactSameProduct && input.exactSameTaxesAndFees && input.exactSameCancellationTerms && input.exactSameRestrictions;
  const differenceMinor = Math.max(0, input.portabilityTotalMinor - input.competitorTotalMinor);
  const approved = decision.approve && equivalent && differenceMinor > 0;
  return Object.freeze({
    requestId: input.requestId,
    status: approved ? "APPROVED" as const : "REJECTED" as const,
    equivalent,
    rewardType: "PORTABILITY_POINTS" as const,
    pointsAwarded: approved ? differenceMinor : 0,
    cashPayoutMinor: 0,
    reviewerId: decision.reviewerId,
    decidedAt: decision.decidedAt
  });
}

export const VERIFIED_SAVINGS_SOURCES = ["MEMBER_RATE", "PORTABILITY_DEAL", "POINTS_REDEMPTION", "PRICE_MATCH"] as const;
export type VerifiedSavingsSource = (typeof VERIFIED_SAVINGS_SOURCES)[number];

export function recordVerifiedSavings(input: { readonly activityId: string; readonly accountId: string; readonly source: VerifiedSavingsSource; readonly amountMinor: number; readonly currency: string; readonly evidenceReference: string; readonly recordedAt: string }) {
  if (!(VERIFIED_SAVINGS_SOURCES as readonly string[]).includes(input.source)) throw new Error("Savings source is not supported");
  minor(input.amountMinor, "amountMinor");
  currency(input.currency);
  requireText(input.evidenceReference, "evidenceReference");
  canonicalIso(input.recordedAt, "recordedAt");
  return Object.freeze({ ...input, estimated: false as const, verified: true as const });
}

export interface CommerceActivityEvent {
  readonly activityId: string;
  readonly accountId: string;
  readonly kind: "BOOKING" | "PURCHASE" | "CLAIMED_OFFER" | "TICKET_VOUCHER" | "POINTS" | "REFERRAL_EARNING" | "PAYOUT" | "REFUND" | "VERIFIED_SAVINGS";
  readonly occurredAt: string;
  readonly status: string;
  readonly reference: string;
  readonly amountMinor: number | null;
  readonly currency: string | null;
  readonly verified: boolean;
}

export function projectMyActivity(events: readonly CommerceActivityEvent[], accountId: string) {
  requireText(accountId, "accountId");
  const own = events.filter((event) => event.accountId === accountId).map((event) => {
    canonicalIso(event.occurredAt, "activity occurredAt");
    requireText(event.reference, "activity reference");
    if (event.amountMinor !== null) minor(event.amountMinor, "activity amountMinor");
    if (event.currency !== null) currency(event.currency);
    if (event.kind === "VERIFIED_SAVINGS" && !event.verified) throw new Error("Savings activity must be verified");
    return Object.freeze({ ...event });
  }).sort((left, right) => right.occurredAt.localeCompare(left.occurredAt));
  return Object.freeze({
    accountId,
    events: Object.freeze(own),
    verifiedSavingsMinorByCurrency: Object.freeze(Object.fromEntries([...new Set(own.filter((event) => event.kind === "VERIFIED_SAVINGS").map((event) => event.currency).filter((value): value is string => value !== null))].map((code) => [code, own.filter((event) => event.kind === "VERIFIED_SAVINGS" && event.currency === code).reduce((sum, event) => sum + (event.amountMinor ?? 0), 0)]))),
    fabricatedSavingsIncluded: false as const
  });
}

export interface EventListing {
  readonly eventId: string;
  readonly title: string;
  readonly venueKey: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly source: string;
  readonly sourceReference: string;
  readonly familyFriendly: boolean;
}

export function mergeEvents(events: readonly EventListing[]) {
  const seen = new Set<string>();
  return Object.freeze(events
    .filter((event) => {
      canonicalIso(event.startsAt, "event startsAt");
      canonicalIso(event.endsAt, "event endsAt");
      if (Date.parse(event.endsAt) <= Date.parse(event.startsAt)) throw new Error("Event must end after it starts");
      const key = `${event.title.trim().toLocaleLowerCase()}:${event.venueKey}:${event.startsAt}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => left.startsAt.localeCompare(right.startsAt)));
}

export function visibleEventsAt(events: readonly EventListing[], now: string) {
  canonicalIso(now, "now");
  return Object.freeze(mergeEvents(events).filter((event) => Date.parse(event.endsAt) > Date.parse(now)));
}

export function createEventReminders(event: EventListing, preferences: { readonly twentyFourHours: boolean; readonly oneHour: boolean }) {
  const startsAt = Date.parse(canonicalIso(event.startsAt, "event startsAt"));
  return Object.freeze([
    ...(preferences.twentyFourHours ? [{ kind: "24_HOURS" as const, scheduledAt: new Date(startsAt - 86_400_000).toISOString() }] : []),
    ...(preferences.oneHour ? [{ kind: "1_HOUR" as const, scheduledAt: new Date(startsAt - 3_600_000).toISOString() }] : [])
  ].map((reminder) => Object.freeze({ ...reminder, channel: "PUSH_AND_IN_APP" as const, customTimeAllowed: false as const, calendarIntegration: false as const })));
}

export type EventReminderKind = ReturnType<typeof createEventReminders>[number]["kind"];
export type EventReminderStatus = "SCHEDULED" | "DUE" | "IN_APP_DELIVERED" | "DISABLED" | "EXPIRED";

export interface SavedEventReminder {
  readonly reminderId: string;
  readonly accountId: string;
  readonly event: EventListing;
  readonly reminderKind: EventReminderKind;
  readonly scheduledAt: string;
  readonly channel: "PUSH_AND_IN_APP";
  readonly status: EventReminderStatus;
  readonly disabledAt: string | null;
  readonly inAppDeliveredAt: string | null;
  readonly pushDelivery: {
    readonly providerConfigured: false;
    readonly apnsDelivered: false;
    readonly fcmDelivered: false;
    readonly status: "EXTERNAL_PROVIDER_NOT_CONFIGURED";
    readonly reason: string;
  };
  readonly customTimeAllowed: false;
  readonly calendarIntegration: false;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export function createSavedEventReminders(input: {
  readonly accountId: string;
  readonly event: EventListing;
  readonly preferences: { readonly twentyFourHours: boolean; readonly oneHour: boolean };
  readonly now: string;
}) {
  requireText(input.accountId, "accountId");
  canonicalIso(input.now, "now");
  const event = Object.freeze({ ...input.event, startsAt: canonicalIso(input.event.startsAt, "event startsAt"), endsAt: canonicalIso(input.event.endsAt, "event endsAt") });
  if (Date.parse(event.endsAt) <= Date.parse(event.startsAt)) throw new Error("Event must end after it starts");
  return Object.freeze(createEventReminders(event, input.preferences).map((reminder) => Object.freeze({
    reminderId: `${input.accountId}:${event.eventId}:${reminder.kind}`,
    accountId: input.accountId,
    event,
    reminderKind: reminder.kind,
    scheduledAt: reminder.scheduledAt,
    channel: reminder.channel,
    status: (Date.parse(reminder.scheduledAt) <= Date.parse(input.now) ? "DUE" : "SCHEDULED") as EventReminderStatus,
    disabledAt: null,
    inAppDeliveredAt: null,
    pushDelivery: Object.freeze({
      providerConfigured: false as const,
      apnsDelivered: false as const,
      fcmDelivered: false as const,
      status: "EXTERNAL_PROVIDER_NOT_CONFIGURED" as const,
      reason: "External APNs/FCM reminder delivery is not active until the production push provider is configured and verified."
    }),
    customTimeAllowed: false as const,
    calendarIntegration: false as const,
    createdAt: input.now,
    updatedAt: input.now
  })));
}

export function createVerifiedReview(input: { readonly reviewId: string; readonly accountId: string; readonly targetId: string; readonly completedTransactionId: string | null; readonly rating: number; readonly comment?: string; readonly source: "PORTABILITY_VERIFIED" | "PROVIDER_ATTRIBUTED"; readonly createdAt: string }) {
  if (!Number.isInteger(input.rating) || input.rating < 1 || input.rating > 5) throw new Error("Review rating must be an integer from 1 to 5");
  if (input.source === "PORTABILITY_VERIFIED" && input.completedTransactionId === null) throw new Error("Verified reviews require a completed transaction");
  canonicalIso(input.createdAt, "createdAt");
  return Object.freeze({ ...input, verified: input.completedTransactionId !== null, anonymous: false as const, moderationStatus: "PENDING" as const });
}

export function calculateLocalGuideSettlement(input: { readonly grossMinor: number; readonly currency: string; readonly refundedMinor?: number }) {
  minor(input.grossMinor, "grossMinor");
  minor(input.refundedMinor ?? 0, "refundedMinor");
  currency(input.currency);
  if ((input.refundedMinor ?? 0) > input.grossMinor) throw new Error("Refund cannot exceed gross");
  const retainedMinor = input.grossMinor - (input.refundedMinor ?? 0);
  const platformFeeMinor = Math.floor(retainedMinor * 0.10);
  return Object.freeze({ currency: input.currency, retainedMinor, platformFeeMinor, guideEarningMinor: retainedMinor - platformFeeMinor, feeBasisPoints: 1_000 });
}

export function calculateVipSettlement(input: { readonly grossMinor: number; readonly currency: string; readonly charityMode: boolean; readonly unavoidableProcessingMinor?: number; readonly charityVerified: boolean }) {
  minor(input.grossMinor, "grossMinor");
  minor(input.unavoidableProcessingMinor ?? 0, "unavoidableProcessingMinor");
  currency(input.currency);
  if (input.charityMode && !input.charityVerified) throw new Error("Charity mode requires a verified cause");
  const platformFeeMinor = input.charityMode ? 0 : Math.floor(input.grossMinor * 0.10);
  const hostFeeMinor = 0;
  return Object.freeze({
    currency: input.currency,
    charityMode: input.charityMode,
    platformFeeMinor,
    hostFeeMinor,
    buyerFeeMinor: 0,
    causeTargetMinor: input.charityMode ? input.grossMinor : 0,
    sellerProceedsMinor: input.charityMode ? 0 : input.grossMinor - platformFeeMinor,
    processingAbsorbedByPortabilityMinor: input.charityMode ? (input.unavoidableProcessingMinor ?? 0) : 0
  });
}

export interface VipBid {
  readonly accountId: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly placedAt: string;
  readonly eligible: boolean;
}

export function createVipBid(input: VipBid, closesAt: string) {
  minor(input.amountMinor, "bid amountMinor");
  currency(input.currency);
  canonicalIso(input.placedAt, "placedAt");
  canonicalIso(closesAt, "closesAt");
  if (Date.parse(input.placedAt) >= Date.parse(closesAt)) throw new Error("VIP auction is closed");
  if (!input.eligible) throw new Error("VIP bidding requires eligible Insider or Creator access");
  return Object.freeze({ ...input, depositMinor: 0 as const });
}

export function selectVipWinner(bids: readonly VipBid[], input: { readonly closesAt: string; readonly paymentWindowEndsAt: string }) {
  canonicalIso(input.closesAt, "closesAt");
  canonicalIso(input.paymentWindowEndsAt, "paymentWindowEndsAt");
  if (Date.parse(input.paymentWindowEndsAt) <= Date.parse(input.closesAt)) throw new Error("VIP payment window must follow auction close");
  const eligible = [...bids].filter((bid) => bid.eligible).sort((left, right) => right.amountMinor - left.amountMinor || left.placedAt.localeCompare(right.placedAt));
  const winner = eligible[0] ?? null;
  return Object.freeze({
    winner,
    alternates: Object.freeze(eligible.slice(1)),
    experienceConfirmed: false as const,
    paymentRequiredBeforeConfirmation: true as const,
    paymentWindowEndsAt: input.paymentWindowEndsAt
  });
}

export function resolveVipWinnerPayment(selection: ReturnType<typeof selectVipWinner>, paid: boolean) {
  if (selection.winner === null) return selection;
  if (paid) return Object.freeze({ ...selection, experienceConfirmed: true as const });
  const [next, ...remaining] = selection.alternates;
  return Object.freeze({
    ...selection,
    winner: next ?? null,
    alternates: Object.freeze(remaining),
    experienceConfirmed: false as const,
    previousWinnerClaimLost: true as const
  });
}

export function vipAccessForTier(tier: CommerceTier) {
  return Object.freeze({
    browse: true as const,
    requestBookOrBid: tier !== "FREE",
    selectedPriorityAccess: tier === "CREATOR" || tier === "LIFETIME_CREATOR"
  });
}

export interface PollVote {
  readonly accountId: string;
  readonly optionId: string;
  readonly castAt: string;
}

export function castOrChangeVote(votes: readonly PollVote[], input: PollVote, closesAt: string) {
  canonicalIso(input.castAt, "castAt");
  canonicalIso(closesAt, "closesAt");
  if (Date.parse(input.castAt) >= Date.parse(closesAt)) throw new Error("Poll is closed");
  requireText(input.accountId, "accountId");
  requireText(input.optionId, "optionId");
  return Object.freeze([...votes.filter((vote) => vote.accountId !== input.accountId), Object.freeze(input)]);
}

export function projectPoll(votes: readonly PollVote[], input: { readonly accountId: string; readonly options: readonly string[]; readonly closesAt: string; readonly now: string }) {
  canonicalIso(input.closesAt, "closesAt");
  canonicalIso(input.now, "now");
  const ownVote = votes.find((vote) => vote.accountId === input.accountId) ?? null;
  const canSeeResults = ownVote !== null || Date.parse(input.now) >= Date.parse(input.closesAt);
  const results = canSeeResults
    ? Object.freeze(Object.fromEntries(input.options.map((optionId) => [optionId, votes.filter((vote) => vote.optionId === optionId).length])))
    : null;
  return Object.freeze({ ownVote, canSeeResults, results, commentsEnabled: false as const, closed: Date.parse(input.now) >= Date.parse(input.closesAt) });
}

export function createEmergencyHelpRecord(input: { readonly country: string; readonly kind: "PHARMACY" | "URGENT_CARE" | "HOSPITAL" | "POLICE" | "EMBASSY" | "CONSULATE" | "LOST_AND_FOUND"; readonly name: string; readonly phone: string | null; readonly directionsTarget: string | null; readonly source: string; readonly sourceCheckedAt: string }) {
  if (!/^[A-Z]{2}$/.test(input.country)) throw new Error("Emergency help country must be ISO alpha-2");
  requireText(input.name, "name");
  requireText(input.source, "source");
  canonicalIso(input.sourceCheckedAt, "sourceCheckedAt");
  if (input.phone === null && input.directionsTarget === null) throw new Error("Emergency help requires a call or directions action");
  return Object.freeze({ ...input, medicalAdvice: false as const, legalAdvice: false as const, disclaimer: "Basic local information only; verify current details and contact local authorities for emergencies." });
}
